import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  effect
} from "@angular/core";
import { ExtenderService } from "src/app/services/extender.service";
import { FormService } from "src/app/services/form.service";
import { PriceService } from "src/app/services/price.service";
import { TrackingService } from "src/app/services/tracking.service";
import { PackageTagEnum, PackageResponse } from "src/domain/client";
import { PageBaseComponent } from "../../base/page-base/page-base.component";
import { FormGroup } from "@angular/forms";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import {
  createExtraServicesForm,
  filterAdditionalPackages
} from "./extra-services-form.helper";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { LoadingComponent } from "../../layout/loading/loading.component";
import { RequestFailedComponent } from "../../layout/request-failed/request-failed.component";
import { PackageContainerComponent } from "../../layout/package-container/package-container.component";
import { TranslateModule } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import {
  AppError,
  loadExtraServicesData,
  selectError,
  selectIsLoading,
  selectServiceAdditionalPackagesStep5,
  updateSelectedPackages
} from "src/app/state";
import { toSignal } from "@angular/core/rxjs-interop";
import { debounceTime, switchMap } from "rxjs";

@Component({
  selector: "app-extra-services",
  templateUrl: "./extra-services.component.html",
  imports: [
    PageContainerComponent,
    LoadingComponent,
    RequestFailedComponent,
    PackageContainerComponent,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExtraServicesComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  public PackageTagEnum: typeof PackageTagEnum = PackageTagEnum;

  readonly extraServicesPackages: Signal<PackageResponse[] | null>;

  readonly isLoading: Signal<boolean>;
  readonly error: Signal<AppError | null>;

  constructor(
    public formService: FormService,
    public extenderService: ExtenderService,
    private trackingService: TrackingService,
    public priceService: PriceService,
    private businessProposalService: BusinessProposalService,
    private store: Store
  ) {
    super();
    this.formService.extraServicesForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.extraServicesForm.value;

    this.isLoading = this.store.selectSignal(selectIsLoading);
    this.error = this.store.selectSignal(selectError);

    this.extraServicesPackages = this.store.selectSignal(
      selectServiceAdditionalPackagesStep5
    );

    const shouldCreateNewForm =
      this.formService.extraServicesForm.controls.invalidateByDefault;

    effect(() => {
      const packages = this.extraServicesPackages();

      if (
        !this.isLoading() &&
        !this.error() &&
        packages &&
        shouldCreateNewForm
      ) {
        const proposal = this.businessProposalService.businessProposal$.value;
        this.formService.additionalPackagesResponse = filterAdditionalPackages(
          this.businessProposalService.extraServicePackages,
          packages,
          proposal
        );

        const newForm = createExtraServicesForm(packages, {});
        this.formService.updateForm(newForm);
        this.formService.extraServicesForm.markAsTouched();
      }
    });

    const formChanges = toSignal(
      this.formService.extraServicesForm$.pipe(
        switchMap((form) => form.valueChanges.pipe(debounceTime(300)))
      )
    );

    effect(() => {
      const changes = formChanges();
      if (changes) {
        const rawValue = this.formService.extraServicesForm.getRawValue();
        this.store.dispatch(
          updateSelectedPackages({
            selectedPackages: { additionalPackagesForm: rawValue }
          })
        );
      }
    });
  }

  ngOnInit(): void {
    this.formService.extraServicesForm.markAsPristine();

    if (!this.extraServicesPackages().length) {
      this.store.dispatch(loadExtraServicesData());
      this.trackingService.hasTrackedAddonsOnce = false;
    }
  }

  extraServicesParentForm(id: string): FormGroup<any> {
    return this.formService.extraServicesForm.get(id) as FormGroup<any>;
  }

  ngOnDestroy(): void {
    if (
      !this.formService.extraServicesForm.pristine &&
      !this.trackingService.hasTrackedAddonsOnce
    )
      this.trackingService.trackAddonChosen(
        this.formService.getAdditionalSelectionAsProducts()
      );
    this.cleanUp();
  }
}
